import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import type { EcosystemSummary, Me, OrgKind, Organization, OrganizationDetail, Tag, TeamRole } from '../lib/api';
import { fmtDateTime, splitList, titleCase } from '../lib/format';
import { Empty, Kpi, Modal, PageHead, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { ScoreBadge } from '../components/matches';

const KINDS: OrgKind[] = ['company', 'university', 'lab', 'government', 'consortium', 'nonprofit', 'other'];
const ROLES: TeamRole[] = ['provider', 'integrator', 'research', 'manufacturing', 'test', 'convener', 'sponsor'];

export function Ecosystem({ params, me }: { params: string[]; me: Me }) {
  const routeId = params[0] === 'id' ? (params[1] ?? null) : null;
  const [data, setData] = useState<{ items: Organization[]; summary: EcosystemSummary } | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [kind, setKind] = useState('');
  const [state, setState] = useState('');
  const [tag, setTag] = useState('');
  const [claimed, setClaimed] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<string | null>(routeId);
  const canEdit = me.role !== 'viewer';

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (kind) p.set('kind', kind);
    if (state) p.set('state', state);
    if (tag) p.set('tag', tag);
    if (claimed) p.set('claimed', '1');
    if (q) p.set('q', q);
    api<{ items: Organization[]; summary: EcosystemSummary }>(`/api/ecosystem/organizations?${p}`).then(setData).catch(() => setData(null));
  }, [kind, state, tag, claimed, q]);
  useEffect(load, [load]);
  useEffect(() => { api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([])); }, []);
  useEffect(() => { setSelected(routeId); }, [routeId]);
  const closeDetail = useCallback(() => { setSelected(null); if (routeId) location.hash = '/ecosystem'; }, [routeId]);

  const s = data?.summary;
  const states = Object.entries(s?.by_state ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <>
      <PageHead
        title="Ecosystem Map"
        sub="Companies, universities, labs, test resources, manufacturers and primes: what exists, where it is, and how it could combine against a mission need"
        actions={
          <>
            <form className="search" onSubmit={(e: FormEvent) => { e.preventDefault(); setQ(draft.trim()); }} style={{ minWidth: 240 }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Name, description, city…" />
            </form>
            {canEdit && <button className="primary-btn" onClick={() => setAdding(true)}>+ Add organization</button>}
          </>
        }
      />
      <div className="kpi-row">
        <Kpi label="Organizations" value={s?.organizations ?? '…'} />
        <Kpi label="With capability claims" value={s?.with_claims ?? '…'} hint="candidates for team formation" />
        <Kpi label="Capability claims" value={s?.claims ?? '…'} />
        <Kpi label="Companies" value={s?.by_kind?.company ?? 0} />
        <Kpi label="Universities and labs" value={(s?.by_kind?.university ?? 0) + (s?.by_kind?.lab ?? 0) + (s?.by_kind?.nonprofit ?? 0)} />
        <Kpi label="Government" value={s?.by_kind?.government ?? 0} hint="sponsors and test partners" />
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${kind === '' ? 'active' : ''}`} onClick={() => setKind('')}>All kinds</button>
        {KINDS.map((k) => <button key={k} className={`filter-btn ${kind === k ? 'active' : ''}`} onClick={() => setKind(k)}>{titleCase(k)}</button>)}
        <span className="filter-gap" />
        <button className={`filter-btn ${state === '' ? 'active' : ''}`} onClick={() => setState('')}>Any state</button>
        {states.slice(0, 8).map(([st, n]) => <button key={st} className={`filter-btn ${state === st ? 'active' : ''}`} onClick={() => setState(st === '—' ? '' : st)}>{st} <span className="count">{n}</span></button>)}
        <span className="filter-gap" />
        <button className={`filter-btn ${claimed ? 'active' : ''}`} onClick={() => setClaimed(!claimed)}>With claims</button>
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${tag === '' ? 'active' : ''}`} onClick={() => setTag('')}>Any technology</button>
        {tags.map((t) => <button key={t.id} className={`filter-btn ${tag === t.id ? 'active' : ''}`} onClick={() => setTag(t.id)}>{t.label}</button>)}
      </div>

      <div className="panel">
        <div className="panel-body table-wrap">
          {!data ? <Empty>Loading…</Empty> : data.items.length === 0 ? (
            <Empty>No organizations match.{canEdit && ' Add one, then record what it can do.'}</Empty>
          ) : (
            <table className="data-table">
              <thead><tr><th>Organization</th><th>Kind</th><th>Roles</th><th>Technology</th><th className="num">Claims</th><th className="num">TRL</th><th className="num">Needs</th></tr></thead>
              <tbody>
                {data.items.map((o) => (
                  <tr key={o.id} className="clickable" onClick={() => setSelected(o.id)}>
                    <td><strong>{o.name}</strong>{o.is_prime && <span className="tag new">PRIME</span>}<div className="muted sm">{[o.city, o.state].filter(Boolean).join(', ')}{o.client_profile_id && <> · <a className="link" href={`#/clients/${o.client_profile_id}`} onClick={(e) => e.stopPropagation()}>client profile</a></>}</div></td>
                    <td>{titleCase(o.kind)}</td>
                    <td className="muted sm">{o.roles.map(titleCase).join(', ') || '—'}</td>
                    <td>{o.claim_tags.length || o.tags.length ? <TagChips tags={o.claim_tags.length ? o.claim_tags : o.tags} all={tags} max={3} /> : <span className="muted">—</span>}</td>
                    <td className="num">{o.claims}</td>
                    <td className="num">{o.trl ?? '—'}</td>
                    <td className="num">{o.need_matches > 0 ? <span className="pos">{o.need_matches}</span> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {adding && <OrgForm onClose={() => setAdding(false)} onSaved={(id) => { setAdding(false); load(); setSelected(id); }} />}
      {selected && <OrgModal id={selected} me={me} tags={tags} onClose={closeDetail} onChanged={load} />}
    </>
  );
}

function OrgForm({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState({ name: '', kind: 'company' as OrgKind, city: '', state: '', website: '', description: '', is_prime: false });
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await api<{ id: string }>('/api/ecosystem/organizations', { method: 'POST', json: { name: f.name.trim(), kind: f.kind, city: f.city.trim() || undefined, state: f.state.trim() || undefined, website: f.website.trim() || undefined, description: f.description.trim() || undefined, is_prime: f.is_prime } });
      onSaved(r.id);
    } catch (ex) { setErr(ex instanceof ApiError && ex.status === 409 ? 'An organization with that name already exists.' : (ex as Error).message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Add organization" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="form-grid">
          <div className="field span-2"><label htmlFor="og-name">Name</label><input id="og-name" className="input" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="field"><label htmlFor="og-kind">Kind</label><select id="og-kind" className="select" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as OrgKind })}>{KINDS.map((k) => <option key={k} value={k}>{titleCase(k)}</option>)}</select></div>
          <div className="field"><label>Prime / integrator</label><button type="button" className={`tag-toggle ${f.is_prime ? 'on' : ''}`} onClick={() => setF({ ...f, is_prime: !f.is_prime })}>{f.is_prime ? 'Yes' : 'No'}</button></div>
          <div className="field"><label htmlFor="og-city">City</label><input id="og-city" className="input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
          <div className="field"><label htmlFor="og-state">State</label><input id="og-state" className="input" maxLength={10} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} placeholder="MA" /></div>
          <div className="field span-2"><label htmlFor="og-web">Website</label><input id="og-web" className="input" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} /></div>
          <div className="field span-2"><label htmlFor="og-desc">Description</label><textarea id="og-desc" className="textarea" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        </div>
        {err && <div className="error-text">{err}</div>}
        <div className="form-actions"><button type="button" className="ghost-btn" onClick={onClose}>Cancel</button><button type="submit" className="primary-btn" disabled={saving || f.name.trim().length < 2}>{saving ? 'Saving…' : 'Add'}</button></div>
      </form>
    </Modal>
  );
}

function OrgModal({ id, me, tags, onClose, onChanged }: { id: string; me: Me; tags: Tag[]; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<OrganizationDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [claim, setClaim] = useState({ capability: '', trl: '', facilities: '', geography: '', evidence: '' });
  const [claimTags, setClaimTags] = useState<string[]>([]);
  const [claimRoles, setClaimRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const canEdit = me.role !== 'viewer';

  const load = useCallback(() => { api<OrganizationDetail>(`/api/ecosystem/organizations/${id}`).then(setD).catch((e: Error) => setErr(e.message)); }, [id]);
  useEffect(load, [load]);
  const toggle = (list: string[], set: (v: string[]) => void, v: string) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  async function addClaim(e: FormEvent) {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      await api(`/api/ecosystem/organizations/${id}/claims`, { method: 'POST', json: { capability: claim.capability.trim(), technology_tags: claimTags, trl: claim.trl ? Number(claim.trl) : null, roles: claimRoles, facilities: claim.facilities.trim() || undefined, geography: claim.geography.trim() || undefined, evidence: claim.evidence.trim() || undefined } });
      setClaim({ capability: '', trl: '', facilities: '', geography: '', evidence: '' }); setClaimTags([]); setClaimRoles([]);
      load(); onChanged();
    } catch (ex) { setErr((ex as Error).message); } finally { setSaving(false); }
  }
  async function removeClaim(claimId: string) { try { await api(`/api/ecosystem/claims/${claimId}`, { method: 'DELETE' }); load(); onChanged(); } catch (ex) { setErr((ex as Error).message); } }
  async function addNote(e: FormEvent) { e.preventDefault(); if (!note.trim()) return; try { await api(`/api/ecosystem/organizations/${id}/notes`, { method: 'POST', json: { body: note.trim() } }); setNote(''); load(); } catch (ex) { setErr((ex as Error).message); } }

  return (
    <Modal title={d?.name ?? 'Loading…'} onClose={onClose}>
      {err && <div className="error-text">{err}</div>}
      {d && (
        <>
          <div className="filter-bar" style={{ marginBottom: 12 }}>
            <Pill kind="info">{titleCase(d.kind)}</Pill>
            {d.is_prime && <Pill kind="warn">Prime</Pill>}
            {(d.city || d.state) && <Pill kind="muted">{[d.city, d.state].filter(Boolean).join(', ')}</Pill>}
            {d.website && <a className="link" href={d.website} target="_blank" rel="noreferrer">Website ↗</a>}
            {d.clients.map((c) => <a key={c.id} className="link" href={`#/clients/${c.id}`}>Client profile: {c.name} →</a>)}
          </div>
          {d.description && <p style={{ fontSize: 13, marginBottom: 10 }}>{d.description}</p>}
          <TagChips tags={d.tags} all={tags} max={12} />

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Capability claims ({d.claims.length})</h3>
          {d.claims.length === 0 && <div className="muted sm">Nothing recorded yet. Claims make this organization a candidate for mission needs.</div>}
          {d.claims.map((c) => (
            <div className="version" key={c.id} style={{ alignItems: 'flex-start' }}>
              <time>{c.trl ? `TRL ${c.trl}` : '—'}</time>
              <span style={{ flex: 1 }}>
                <strong>{c.capability}</strong>
                <div className="muted sm">{[c.roles.map(titleCase).join(', '), c.facilities, c.geography].filter(Boolean).join(' · ')}</div>
                <div style={{ marginTop: 3 }}><TagChips tags={c.technology_tags} all={tags} max={6} /></div>
                {c.evidence && <div className="muted sm" style={{ marginTop: 3 }}>{c.evidence}</div>}
              </span>
              {canEdit && <button className="ghost-btn ack-btn" title="Remove claim" onClick={() => removeClaim(c.id)}>✕</button>}
            </div>
          ))}
          {canEdit && (
            <form onSubmit={addClaim} style={{ marginTop: 12 }}>
              <div className="form-grid">
                <div className="field span-2"><label htmlFor="cl-cap">Add a capability claim</label><input id="cl-cap" className="input" required minLength={3} value={claim.capability} onChange={(e) => setClaim({ ...claim, capability: e.target.value })} placeholder="e.g. Rad-hard assembly and known-good-die test" /></div>
                <div className="field span-2"><label>Technology domains</label><div className="tag-list">{tags.map((t) => <button type="button" key={t.id} className={`tag-toggle ${claimTags.includes(t.id) ? 'on' : ''}`} onClick={() => toggle(claimTags, setClaimTags, t.id)}>{t.label}</button>)}</div></div>
                <div className="field span-2"><label>Roles it could play</label><div className="tag-list">{ROLES.map((r) => <button type="button" key={r} className={`tag-toggle ${claimRoles.includes(r) ? 'on' : ''}`} onClick={() => toggle(claimRoles, setClaimRoles, r)}>{titleCase(r)}</button>)}</div></div>
                <div className="field"><label htmlFor="cl-trl">TRL</label><input id="cl-trl" className="input" type="number" min={1} max={9} value={claim.trl} onChange={(e) => setClaim({ ...claim, trl: e.target.value })} /></div>
                <div className="field"><label htmlFor="cl-geo">Geography</label><input id="cl-geo" className="input" value={claim.geography} onChange={(e) => setClaim({ ...claim, geography: e.target.value })} /></div>
                <div className="field span-2"><label htmlFor="cl-fac">Facilities / infrastructure</label><input id="cl-fac" className="input" value={claim.facilities} onChange={(e) => setClaim({ ...claim, facilities: e.target.value })} /></div>
                <div className="field span-2"><label htmlFor="cl-ev">Evidence</label><input id="cl-ev" className="input" value={claim.evidence} onChange={(e) => setClaim({ ...claim, evidence: e.target.value })} placeholder="Program page, award, site visit, conversation…" /></div>
              </div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={saving || claim.capability.trim().length < 3}>{saving ? 'Saving…' : 'Add claim'}</button></div>
            </form>
          )}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Mission needs this organization fits ({d.needs.length})</h3>
          {d.needs.length === 0 && <div className="muted sm">No need scores against this organization yet.</div>}
          {d.needs.map((n) => (
            <div className="version" key={n.match_id}><time><ScoreBadge score={n.score} /></time><span><a className="link" href={`#/mission-needs/${n.need_id}`}>{n.title}</a> <span className="muted sm">· {titleCase(n.status)} · {n.role ? titleCase(n.role) : ''} · {titleCase(n.decision)}</span></span></div>
          ))}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Notes ({d.notes.length})</h3>
          {d.notes.map((n) => <div className="note" key={n.id}><div>{n.body}</div><div className="meta">{n.author ?? 'Unknown'} · {fmtDateTime(n.created_at)}</div></div>)}
          {canEdit && (
            <form onSubmit={addNote} style={{ marginTop: 10 }}>
              <div className="field"><textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Relationship, contacts, teaming history…" /></div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={!note.trim()}>Save note</button></div>
            </form>
          )}
        </>
      )}
    </Modal>
  );
}

// splitList is reserved for a future bulk-import form; keep the import used.
void splitList;
